import PermissionButton from 'components/buttons/PermissionButton';
import ContactPickerModal from 'components/forms/form-components/select-picker/ContactPickerModal';
import DynamicMultiSelectInput from 'components/forms/inputs/rhf-inputs/DynamicMultiSelectInput';
import MultiSelectInput from 'components/forms/inputs/rhf-inputs/MultiSelectInput';
import RadioInput from 'components/forms/inputs/rhf-inputs/RadioInput';
import SelectInput from 'components/forms/inputs/rhf-inputs/SelectInput';
import TextAreaInput from 'components/forms/inputs/rhf-inputs/TextAreaInput';
import TextInput from 'components/forms/inputs/rhf-inputs/TextInput';
import InfoTooltip from 'components/InfoTooltip';
import ContactDynamicMultiSelectPicker from 'components/party/ContactDynamicMultiSelectPicker';
import { FormattedMessage } from 'react-intl';
import {
    useLazyGetMinContactsListWithFiltersQuery,
    useLazyGetMinContactsWithRelationsListWithFiltersQuery,
} from 'store/api/contacts';
import { useLazyGetEmployeesListWithFiltersQuery } from 'store/api/employees';
import { ListedIn } from 'types/back-office-settings';
import { ContactMinDtoWithRelationsDto, PartyRelationshipTypeCode, PartyType } from 'types/contacts';
import { GroupType } from 'types/groups';
import { getValidOptionsFromClassifier } from 'utils/formUtil';
import { t } from 'utils/getTranslations';
import { GroupFormState } from './useGroupForm';

type Props = { form: GroupFormState };

const GROUP_TYPES = [
    {
        value: GroupType.CORPORATE,
        label: t('groups.types.corporate'),
        tooltip: <InfoTooltip text={'groups.tooltips.type.corporate'} />,
    },
    {
        value: GroupType.ALLIANCE,
        label: t('groups.types.alliance'),
        tooltip: <InfoTooltip text={'groups.tooltips.type.alliance'} />,
    },
    {
        value: GroupType.OTHER,
        label: t('groups.types.other'),
        tooltip: <InfoTooltip text={'groups.tooltips.type.other'} />,
    },
];

const CREATE_CONTACT_PERMISSIONS = ['CREATE.PARTIES', 'CREATE.PARTIES.CLIENTS', 'CREATE.PARTIES.LEADS_AND_NON_CLIENTS'];

// The edit page's Group section holds two cards' fields; it composes these pieces in its own order.

export const GroupTypeField = ({ form }: Props): JSX.Element => (
    <RadioInput
        setValue={form.methods.setValue}
        control={form.methods.control}
        label="groups.type"
        name="groupType"
        options={GROUP_TYPES ?? []}
        getOptionLabel="label"
        getOptionIdentifier="value"
        getOptionTooltip="tooltip"
    />
);

export const GroupNameField = ({ form }: Props): JSX.Element => (
    <TextInput control={form.methods.control} name="groupName" label="groups.name" placeholder="groups.name" required />
);

/** Members & relationship: business areas, members, primary contacts, relationship partners and managers. */
export const MembersFields = ({ form }: Props): JSX.Element => {
    const { control, setValue } = form.methods;
    const {
        businessAreaOptions,
        sortedPersonMemberIds,
        sortedCompanyMemberIds,
        openContactTypePicker,
        openAddNewPrimaryContactModal,
    } = form;
    return (
        <>
            <MultiSelectInput
                control={control}
                name="businessAreas"
                label="groups.operates-in-business-areas"
                placeholder="groups.operates-in-business-areas"
                options={businessAreaOptions}
                setValue={setValue}
                getOptionLabel="label"
                getOptionIdentifier="value"
                sortDirection="asc"
                rightLabelComponent={<InfoTooltip text={'groups.tooltips.operates-in-business-areas'} />}
            />
            <ContactDynamicMultiSelectPicker
                setValue={setValue}
                control={control}
                label="groups.group-members"
                placeholder="groups.group-members"
                name="members"
                paginated={{
                    fetch: useLazyGetMinContactsListWithFiltersQuery,
                    searchField: ['name', 'partyNumber'],
                }}
                PickerModalComponent={ContactPickerModal}
                noOptionsContent={
                    <PermissionButton
                        permissions={CREATE_CONTACT_PERMISSIONS}
                        variant="contained"
                        color="secondary"
                        size="extraSmall"
                        onClick={openContactTypePicker}
                    >
                        <FormattedMessage id="contacts.add-new-contact" />
                    </PermissionButton>
                }
            />
            <ContactDynamicMultiSelectPicker
                setValue={setValue}
                control={control}
                label="groups.group-primary-contacts"
                placeholder="groups.group-primary-contacts"
                name="primaryContacts"
                paginated={{
                    fetch:
                        sortedPersonMemberIds.length || sortedCompanyMemberIds.length
                            ? useLazyGetMinContactsWithRelationsListWithFiltersQuery
                            : useLazyGetMinContactsListWithFiltersQuery,
                    filters: `partyType==${PartyType.PERSON}`,
                    searchField: ['name', 'partyNumber'],
                    additionalParams:
                        sortedPersonMemberIds.length || sortedCompanyMemberIds.length
                            ? {
                                  ...(sortedPersonMemberIds.length
                                      ? { prioritizeParties: sortedPersonMemberIds }
                                      : undefined),
                                  ...(sortedCompanyMemberIds.length
                                      ? { prioritizeEmployeesOf: sortedCompanyMemberIds }
                                      : undefined),
                              }
                            : undefined,
                }}
                {...((sortedPersonMemberIds.length || sortedCompanyMemberIds.length) && {
                    groupByStaticFields: (option: ContactMinDtoWithRelationsDto) => {
                        const memberRelated =
                            (option.partyType === PartyType.PERSON && sortedPersonMemberIds.includes(option.id)) ||
                            option.relatesTo?.some(
                                (rel) =>
                                    rel.partyRelationshipTypeCode === PartyRelationshipTypeCode.EMPLOYEE &&
                                    sortedCompanyMemberIds.includes(rel.otherParty.id)
                            );

                        if (memberRelated) {
                            return t('groups.group-primary-contacts-field-groups.member-related-people');
                        }

                        return t('field.other-people');
                    },
                })}
                isPickerModalOptionEnabled={(option) => option.partyType === PartyType.PERSON}
                PickerModalComponent={ContactPickerModal}
                groupSortOrder={[t('groups.group-primary-contacts-field-groups.member-related-people')]}
                defaultGroup={t('groups.group-primary-contacts-field-groups.all-contacts')}
                noOptionsContent={
                    <PermissionButton
                        permissions={CREATE_CONTACT_PERMISSIONS}
                        variant="contained"
                        color="secondary"
                        size="extraSmall"
                        onClick={openAddNewPrimaryContactModal}
                    >
                        <FormattedMessage id="contacts.add-new-contact" />
                    </PermissionButton>
                }
            />
            <DynamicMultiSelectInput
                setValue={setValue}
                control={control}
                label="groups.group-relationship-partners"
                placeholder="groups.group-relationship-partners"
                name="relationshipPartners"
                paginated={{
                    fetch: useLazyGetEmployeesListWithFiltersQuery,
                    searchField: 'fullName',
                    additionalParams: { extraParams: { listedIn: ListedIn.GROUP_RELATIONSHIP_PARTNER } },
                }}
                getOptionLabel="name"
                getOptionIdentifier="id"
            />
            <DynamicMultiSelectInput
                setValue={setValue}
                control={control}
                label="groups.group-relationship-managers"
                placeholder="groups.group-relationship-managers"
                name="relationshipManagers"
                paginated={{
                    fetch: useLazyGetEmployeesListWithFiltersQuery,
                    searchField: 'fullName',
                    additionalParams: { extraParams: { listedIn: ListedIn.GROUP_RELATIONSHIP_MANAGER } },
                }}
                getOptionLabel="name"
                getOptionIdentifier="id"
            />
        </>
    );
};

/** Partnership type, segment (locked by its flag) and service level. */
export const GroupClassificationFields = ({ form }: Props): JSX.Element => {
    const { control, setValue } = form.methods;
    const { classifiers, isSegmentLocked } = form;
    return (
        <>
            <SelectInput
                setValue={setValue}
                control={control}
                label="groups.partnership-type"
                placeholder="groups.partnership-type"
                name="groupPartnershipType"
                options={
                    getValidOptionsFromClassifier<string, string | number>(classifiers['GROUP_PARTNERSHIP_TYPE']) ?? []
                }
                getOptionLabel="label"
                getOptionIdentifier="value"
                sortDirection="asc"
                required
            />
            <SelectInput
                setValue={setValue}
                control={control}
                label="groups.segment-type"
                placeholder="groups.segment-type"
                name="segmentType"
                options={getValidOptionsFromClassifier<string, string>(classifiers['SEGMENT_TYPE']) ?? []}
                getOptionLabel="label"
                getOptionIdentifier="value"
                sortDirection="asc"
                tooltip={<InfoTooltip text="groups.tooltips.segment-type" />}
                disabled={isSegmentLocked}
            />
            <SelectInput
                setValue={setValue}
                control={control}
                label="groups.service-level-type"
                placeholder="groups.service-level-type"
                name="serviceLevelType"
                options={getValidOptionsFromClassifier<string, string>(classifiers['SERVICE_LEVEL_TYPE']) ?? []}
                getOptionLabel="label"
                getOptionIdentifier="value"
                sortDirection="asc"
                tooltip={<InfoTooltip text="groups.tooltips.service-level-type" />}
            />
        </>
    );
};

export const NotesField = ({ form }: Props): JSX.Element => (
    <TextAreaInput
        label="groups.notes"
        control={form.methods.control}
        name="notes"
        placeholder="groups.notes"
        maxRows={5}
        minRows={5}
        enableAutofill
    />
);
